import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { BUSINESS } from '../../constants/business';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#1f2937',
        color: 'white',
        pt: 6,
        pb: 3,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src="/madtitle.png"
              alt={BUSINESS.displayName}
              title="M.A.D. Title Agency logo"
              sx={{ height: 40, width: 'auto', mb: 2, filter: 'brightness(0) invert(1)' }}
            />
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
              Your trusted partner for professional title services in New Jersey.
              We ensure smooth closings and protect your real estate investments.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 2, opacity: 0.7 }}>
              Licensed by the New Jersey Department of Banking and Insurance
            </Typography>
            {(BUSINESS.social.facebook || BUSINESS.social.linkedin) && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {BUSINESS.social.facebook && (
                  <IconButton
                    href={BUSINESS.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'white', '&:hover': { color: '#f59e0b' } }}
                    aria-label="Facebook"
                  >
                    <FacebookIcon />
                  </IconButton>
                )}
                {BUSINESS.social.linkedin && (
                  <IconButton
                    href={BUSINESS.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'white', '&:hover': { color: '#f59e0b' } }}
                    aria-label="LinkedIn"
                  >
                    <LinkedInIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link
                component={RouterLink}
                to="/about"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f59e0b' } }}
              >
                About Us
              </Link>
              <Link
                component={RouterLink}
                to="/services"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f59e0b' } }}
              >
                Services
              </Link>
              <Link
                component={RouterLink}
                to="/resources"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f59e0b' } }}
              >
                Resources
              </Link>
              <Link
                component={RouterLink}
                to="/contact"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f59e0b' } }}
              >
                Contact
              </Link>
            </Box>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Services
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link
                component={RouterLink}
                to="/services/title-insurance"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f59e0b' } }}
              >
                Title Insurance
              </Link>
              <Link
                component={RouterLink}
                to="/services/residential-closings"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f59e0b' } }}
              >
                Residential Closings
              </Link>
              <Link
                component={RouterLink}
                to="/services/commercial-closings"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f59e0b' } }}
              >
                Commercial Closings
              </Link>
              <Link
                component={RouterLink}
                to="/services/title-search"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f59e0b' } }}
              >
                Title Search & Examination
              </Link>
              <Link
                component={RouterLink}
                to="/services/refinance"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f59e0b' } }}
              >
                Refinance Closings
              </Link>
            </Box>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Contact Info
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                <Typography variant="body2">
                  {BUSINESS.address.street}<br />
                  {BUSINESS.address.city}, {BUSINESS.address.state} {BUSINESS.address.zip}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                <Typography variant="body2">
                  <Link
                    href={`tel:${BUSINESS.contact.phone}`}
                    sx={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {BUSINESS.contact.phoneDisplay}
                  </Link>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                <Typography variant="body2">
                  <Link
                    href={`mailto:${BUSINESS.contact.email}`}
                    sx={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {BUSINESS.contact.email}
                  </Link>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                <Typography variant="body2">
                  Mon-Fri: {BUSINESS.hours.monFri}<br />
                  Sat: {BUSINESS.hours.sat} / Sun: {BUSINESS.hours.sun}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Map embed */}
        <Box sx={{ mt: 4, borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
          <iframe
            title="M.A.D. Title Agency Location"
            src={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.address.street + ', ' + BUSINESS.address.city + ', ' + BUSINESS.address.state + ' ' + BUSINESS.address.zip)}&output=embed`}
            width="100%"
            height="200"
            style={{ border: 0, display: 'block' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Box>

        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            © {currentYear} {BUSINESS.legalName}. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link
              href="/privacy-policy"
              sx={{ color: 'inherit', textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms-of-service"
              sx={{ color: 'inherit', textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 